import React from "react";
import { FaLeaf, FaSun, FaSnowflake } from "react-icons/fa";
import { GiMapleLeaf } from "react-icons/gi";
import { resolvePackageIcon } from "./packageIconRegistry";

type SeasonHighlight = {
  title: string;
  duration: string;
  description: string;
  tag: string;
  color: string;
  bg: string;
  iconName?: string;
  iconKey?: string;
};

type SeasonSectionProps = {
  destinationName: string;
  heading?: string;
  subheading?: string;
  highlights?: SeasonHighlight[];
};

const defaultSeasons: SeasonHighlight[] = [
  {
    title: "Spring",
    duration: "March - May",
    description: "Comfortable weather for sightseeing, outdoor touring, and lighter crowds.",
    tag: "Recommended",
    color: "text-green-600",
    bg: "bg-green-50",
    iconKey: "leaf",
  },
  {
    title: "Summer",
    duration: "June - August",
    description: "Lively travel season for signature experiences and full-day exploring.",
    tag: "Peak Travel",
    color: "text-yellow-600",
    bg: "bg-yellow-50",
    iconKey: "sun",
  },
  {
    title: "Autumn",
    duration: "September - November",
    description: "A strong shoulder season with balanced weather and good overall value.",
    tag: "Great Value",
    color: "text-orange-600",
    bg: "bg-orange-50",
    iconKey: "autumn",
  },
  {
    title: "Winter",
    duration: "December - February",
    description: "Best for festive travel, winter landscapes, or quieter off-peak planning.",
    tag: "Seasonal Choice",
    color: "text-blue-600",
    bg: "bg-blue-50",
    iconKey: "snow",
  },
];

const SEASON_STYLES: Record<string, { color: string; bg: string; icon: React.ReactNode }> = {
  spring: { color: "text-green-600",  bg: "bg-green-50",  icon: <FaLeaf /> },
  summer: { color: "text-yellow-600", bg: "bg-yellow-50", icon: <FaSun /> },
  autumn: { color: "text-orange-600", bg: "bg-orange-50", icon: <GiMapleLeaf /> },
  fall:   { color: "text-orange-600", bg: "bg-orange-50", icon: <GiMapleLeaf /> },
  winter: { color: "text-blue-600",   bg: "bg-blue-50",   icon: <FaSnowflake /> },
};

const DEFAULT_STYLE = { color: "text-brand-dark", bg: "bg-gray-50", icon: <FaSun /> };

const getStyle = (season: SeasonHighlight) => {
  const key = (season.title || "").toLowerCase();
  for (const [name, style] of Object.entries(SEASON_STYLES)) {
    if (key.includes(name)) return style;
  }
  return DEFAULT_STYLE;
};

const renderDynamicIcon = (iconToken?: string) => {
  const IconComponent = resolvePackageIcon(iconToken);
  return IconComponent ? <IconComponent /> : null;
};

const renderIcon = (season: SeasonHighlight) => {
  const dynamicIcon = renderDynamicIcon(season.iconName || season.iconKey || season.title);
  if (dynamicIcon) return dynamicIcon;

  if (season.iconKey === "leaf")   return <FaLeaf />;
  if (season.iconKey === "sun")    return <FaSun />;
  if (season.iconKey === "autumn") return <GiMapleLeaf />;
  if (season.iconKey === "snow")   return <FaSnowflake />;
  return getStyle(season).icon;
};

const SeasonSection: React.FC<SeasonSectionProps> = ({
  destinationName,
  heading,
  subheading,
  highlights,
}) => {
  const trimmedHeading = (heading || "").trim();
  const headingContainsDestination =
    trimmedHeading.toLowerCase().includes((destinationName || "").toLowerCase());
  const resolvedHeading = trimmedHeading
    ? headingContainsDestination
      ? trimmedHeading
      : `${trimmedHeading} ${destinationName}`.trim()
    : `Best Time to Visit ${destinationName}`;
  const resolvedSubheading =
    subheading?.trim() ||
    "Plan your trip around weather, crowd levels, and the kind of experience you want most.";
  const resolvedHighlights = highlights && highlights.length > 0 ? highlights : defaultSeasons;

  return (
    <section className="py-16 md:py-20 bg-brand-gray">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <div className="text-center mb-12 md:mb-16">
          <span className="text-brand-pink font-semibold tracking-wider uppercase text-sm mb-2 block">
            Climate & Seasons
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-dark mb-4">
            {resolvedHeading}
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            {resolvedSubheading}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {resolvedHighlights.map((season, index) => {
            const style = getStyle(season);
            const color = season.color || style.color;
            const bg = season.bg || style.bg;

            return (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-md hover-lift border border-gray-100 relative overflow-hidden group"
              >
                <div className={`absolute top-0 right-0 w-24 h-24 ${bg} rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110`} />

                <div className="relative z-10">
                  <div className={`w-12 h-12 rounded-full ${bg} ${color} flex items-center justify-center text-xl mb-4`}>
                    {renderIcon(season)}
                  </div>

                  <h3 className="font-heading text-xl font-bold text-brand-dark mb-1">
                    {season.title || "Season"}
                  </h3>

                  <p className="text-sm text-gray-400 mb-4">{season.duration}</p>

                  <p className="text-gray-600 text-sm mb-4">{season.description}</p>

                  <div className={`flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${color}`}>
                    <span>{season.tag}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
};

export default SeasonSection;
